import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ChevronRight } from "lucide-react";
import { RevealLines, FadeUp, ParallaxImage, EASE } from "../site/motion";
import { ConceptBadge, DemoNote } from "../site/Badges";
import { FEATURES, featurePath } from "../../data/site";

export const DemoPanel = ({ eyebrow = "Interactive demonstration", title, note, live = false, badge, children, id = "demo" }) => (
  <section id={id} data-testid="demo-panel" className="relative mx-auto max-w-[1440px] px-5 py-16 lg:px-12 lg:py-24">
    <FadeUp>
      <div className="glass rounded-[2rem] p-5 sm:p-8 lg:p-12">
        <div className="mb-8 flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
          <div>
            <ConceptBadge label={badge ?? (live ? "Live AI" : eyebrow)} className={live || badge ? "!border-emerald-300/60 !bg-emerald-950/60 !text-emerald-200" : ""} />
            <h2 className="mt-4 font-display text-3xl font-bold text-white sm:text-4xl lg:text-5xl">{title}</h2>
          </div>
          <div className="lg:max-w-md"><DemoNote>{note}</DemoNote></div>
        </div>
        {children}
      </div>
    </FadeUp>
  </section>
);

const Points = ({ f }) => (
  <section className="mx-auto grid max-w-[1440px] gap-4 px-5 pb-16 md:grid-cols-3 lg:px-12">
    {f.points.map(([t, d], i) => (
      <FadeUp key={t} delay={i * 0.1}>
        <div className="h-full border-t border-cyan-400/30 pt-6">
          <p className="font-mono text-xs text-cyan-300">0{i + 1}</p>
          <h3 className="mt-3 font-display text-2xl font-semibold text-white">{t}</h3>
          <p className="mt-2 text-sm text-slate-300">{d}</p>
        </div>
      </FadeUp>
    ))}
  </section>
);

const NextPrev = ({ i }) => {
  const prev = FEATURES[(i + FEATURES.length - 1) % FEATURES.length];
  const next = FEATURES[(i + 1) % FEATURES.length];
  return (
    <section className="mx-auto grid max-w-[1440px] gap-4 px-5 pb-24 sm:grid-cols-2 lg:px-12">
      {[[prev, "Previous", ArrowLeft], [next, "Next", ArrowRight]].map(([f, label, Icon]) => (
        <Link key={label} to={featurePath(f.slug)} data-testid={`feature-${label.toLowerCase()}-link`} className={`glass group flex items-center gap-5 rounded-2xl p-6 transition-[box-shadow] duration-500 hover:shadow-[0_0_40px_rgba(34,211,238,0.25)] ${label === "Next" ? "sm:flex-row-reverse sm:text-right" : ""}`}>
          <Icon className="h-6 w-6 shrink-0 text-cyan-300 transition-transform duration-300 group-hover:scale-125" />
          <div><p className="eyebrow">{label} feature</p><p className="mt-1 font-display text-2xl font-semibold text-white">{f.title}</p></div>
        </Link>
      ))}
    </section>
  );
};

export const FeatureShell = ({ slug, children, cta = "Try the demonstration" }) => {
  const i = FEATURES.findIndex((x) => x.slug === slug);
  const f = FEATURES[i];
  return (
    <div data-testid={`feature-page-${slug}`}>
      <section className="relative mx-auto grid max-w-[1440px] items-center gap-10 px-5 pt-28 lg:grid-cols-12 lg:px-12 lg:pt-40">
        <div className="lg:col-span-5">
          <nav className="mb-8 flex items-center gap-2 text-xs text-slate-300">
            <Link to="/features" data-testid="breadcrumb-features" className="hover:text-cyan-200">Features</Link><ChevronRight className="h-3 w-3" /><span className="text-white">{f.title}</span>
          </nav>
          <p className="eyebrow mb-5 flex items-center gap-3"><f.icon className="h-4 w-4" /> Feature {f.no} / 05</p>
          <RevealLines testId="feature-headline" className="font-display text-5xl font-bold leading-[0.98] tracking-tight text-white sm:text-6xl xl:text-7xl" lines={[{ text: f.lines[0] }, { text: f.lines[1], className: "text-grad" }]} />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.4 }} className="mt-7 text-base leading-relaxed text-slate-200 md:text-lg">{f.intro}</motion.p>
          <a href="#demo" data-testid="try-demo-link" className="btn-primary mt-8">{cta} <ArrowRight className="h-4 w-4" /></a>
        </div>
        <motion.div initial={{ opacity: 0, clipPath: "inset(12% 12% 12% 12% round 2rem)" }} animate={{ opacity: 1, clipPath: "inset(0% 0% 0% 0% round 2rem)" }} transition={{ duration: 1.4, ease: EASE, delay: 0.15 }} className="lg:col-span-7">
          <ParallaxImage src={f.hero} alt={`Honesti — ${f.title}`} className="aspect-[16/11] rounded-[2rem] glow-edge" strength={7} />
        </motion.div>
      </section>
      {children}
      <Points f={f} />
      <NextPrev i={i} />
    </div>
  );
};
